import { useRef, useMemo, useCallback, useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useIntl } from "react-intl";
import axios, { AxiosResponse } from "axios";

import { getCompanyContactBook } from "api";
import { Contact } from "api/models";
import errorToast from "helpers/errorToast";
import testIds from "constants/testIds";
import FormField from "components/molecules/FormField/FormField";
import Checkbox from "components/atoms/Checkbox/Checkbox";
import FormHr from "../Common/FormHr";
import FormItemInfo from "../Common/FormItemInfo";
import SearchSelect from "../Common/SearchSelect";
import { IPickupDeliveryContactProps } from "./Types";
import { handleContactRenderOption } from "./PickupDeliveryAddress/helpers";

function mapContactsToOptions(contacts: Contact[] = []) {
  return contacts.map((contact) => ({
    id: contact.id,
    label: contact.name,
    value: contact.name,
    company_name: contact.company_name,
    phone_number: contact.phone_number,
    email: contact.email,
  }));
}

function PickupDeliveryContact(props: IPickupDeliveryContactProps) {
  const { isOffPlatformShipment, isPickupOrDelivery, scheduleOptions, contactBook, formState, isConsolidated } = props || {};

  const selectedContact = useRef(null);
  const [contactSearchResults, setContactSearchResults] = useState<Contact[] | null>(null);
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);

  const intl = useIntl();
  const methods = useFormContext();
  const { setValue, getValues, control, watch } = methods;
  const invitedModes = useWatch({ name: "invited_modes", control });
  const contactName = useWatch({ name: `schedule.${isPickupOrDelivery}_contact_name`, control });

  useEffect(() => {
    setValue(`schedule.${isPickupOrDelivery}.anonymous`, true);
  }, []);

  const isServerSearch = (contactName?.length || 0) >= 3;

  useEffect(() => {
    if (!isServerSearch) {
      setContactSearchResults(null);
      setIsLoadingContacts(false);
      return undefined;
    }

    const location = getValues("organizational.location") || getValues("shipment.location");
    if (!location) {
      return undefined;
    }

    const controller = new AbortController();
    setIsLoadingContacts(true);

    getCompanyContactBook({
      params: {
        location,
        query: contactName,
      },
      signal: controller.signal,
    })
      .then((res: AxiosResponse) => {
        setContactSearchResults(res.data.results || []);
      })
      .catch((err) => {
        if (!axios.isCancel(err)) {
          errorToast(
            err,
            intl.formatMessage({
              id: "new-request.schedule.couldNotRetrieveContactBook",
              defaultMessage: "Could not retrieve contact book. Please try again later.",
            }),
          );
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoadingContacts(false);
        }
      });

    return () => controller.abort();
  }, [contactName, isServerSearch, getValues, intl]);

  const { allow_anonymous_shipments } = scheduleOptions as any;
  const allowShipment = !watch(`schedule.${isPickupOrDelivery}.anonymous`);

  const effectiveContactBook =
    isServerSearch && contactSearchResults !== null ? contactSearchResults : contactBook || [];

  const contactBookHashMap: any = useMemo(() => {
    const data: any = {};
    if (effectiveContactBook?.length) {
      effectiveContactBook.forEach((contact) => {
        data[contact.name.toLowerCase()] = contact;
      });
    }
    return data;
  }, [effectiveContactBook]);

  const filterContactName = (option: any, itemProps: any, filterKey = "label") => {
    if (selectedContact.current) {
      return true;
    }
    return (
      option[filterKey].toLowerCase().indexOf(itemProps.text.toLowerCase()) !== -1 ||
      option.company_name?.toLowerCase?.().indexOf(itemProps.text.toLowerCase()) !== -1 ||
      option?.email?.toLowerCase?.().indexOf(itemProps.text.toLowerCase()) !== -1
    );
  };

  const autoFillContact = useCallback((event) => {
    const contact = event[0];
    if (contact) {
      setValue(`schedule.${isPickupOrDelivery}_contact_name`, contact.label, { shouldValidate: true });
      setValue(`schedule.${isPickupOrDelivery}_contact_phone`, contact.phone_number, { shouldValidate: true });
      setValue(`schedule.${isPickupOrDelivery}_contact_email`, contact.email, { shouldValidate: true });
    }
  }, []);

  return (
    <>
      {!isOffPlatformShipment ? (
        <>
          <FormHr margin={`${isConsolidated ? "3xl" : "xl"}`} />
          <div className="d-flex mt-3xl mb-3xl">
            <div className="w-200 flex-none mr-xl">
              <FormItemInfo
                labelId={`${
                  isPickupOrDelivery === "pickup"
                    ? "new-request.schedule.pickupContact"
                    : "new-request.schedule.deliveryContact"
                }`}
                label={`${isPickupOrDelivery === "pickup" ? "Pickup contact" : "Delivery contact"}`}
                descId={`${
                  isPickupOrDelivery === "pickup"
                    ? "new-request.schedule.pickupContactInfo"
                    : "new-request.schedule.deliveryContactInfo"
                }`}
                description={`${
                  isPickupOrDelivery === "pickup"
                    ? "Provide your pickup contact details"
                    : "Provide your delivery location details"
                }`}
              />
            </div>

            <div>
              {allow_anonymous_shipments && (
                <div className="mb-md">
                  {!getValues("invited_modes.parcel") && (
                    <Checkbox
                      providerSections
                      checkboxLabel={intl.formatMessage(
                        {
                          id: "settings.contact-list.addTypeContact",
                          defaultMessage: "Add {contactType} contact",
                        },
                        { contactType: isPickupOrDelivery === "stop1" ? "delivery" : isPickupOrDelivery },
                      )}
                      name={`schedule.${isPickupOrDelivery}.anonymous`}
                    />
                  )}
                </div>
              )}
              <div className="flex-row flex-wrap main-wrapper">
                <div className="w-192 dropdown-div">
                  <SearchSelect
                    name={`schedule.${isPickupOrDelivery}_contact_name`}
                    testId={
                      isPickupOrDelivery === "pickup"
                        ? testIds.REQUEST_FORM_SCHEDULE_PICKUP_CONTACT_NAME
                        : testIds.REQUEST_FORM_SCHEDULE_DELIVERY_CONTACT_NAME
                    }
                    enableManualValue
                    required={!allow_anonymous_shipments && !isOffPlatformShipment}
                    label={intl.formatMessage({
                      id: "general.name",
                      defaultMessage: "Name",
                    })}
                    preselect={false}
                    onFocus={() => {
                      if (contactBookHashMap[getValues(`schedule.${isPickupOrDelivery}_contact_name`)?.toLowerCase()]) {
                        selectedContact.current =
                          contactBookHashMap[getValues(`schedule.${isPickupOrDelivery}_contact_name`)?.toLowerCase()];
                      }
                    }}
                    onInputChange={(val: any) => {
                      if (!contactBookHashMap[val?.toLowerCase()]) {
                        selectedContact.current = null;
                      }
                    }}
                    options={mapContactsToOptions(effectiveContactBook)}
                    placeholder={
                      allow_anonymous_shipments && allowShipment
                        ? ""
                        : intl.formatMessage({
                            id: "new-request.schedule.chooseContactPerson",
                            defaultMessage: "Search for a contact person",
                          })
                    }
                    filterBy={isServerSearch ? () => true : filterContactName}
                    isLoading={isLoadingContacts}
                    handleRenderOption={handleContactRenderOption}
                    onSelect={autoFillContact}
                    disable={
                      (allow_anonymous_shipments && allowShipment) ||
                      formState.disabledFields[`schedule.${isPickupOrDelivery}_contact_name`]
                    }
                  />
                </div>

                <div className="w-192">
                  <FormField
                    id="id_pickup_contact_phone"
                    label={intl.formatMessage({
                      id: "new-request.schedule.pickupContactPhone",
                      defaultMessage: "Phone",
                    })}
                    type="textinput"
                    field_id={`schedule.${isPickupOrDelivery}_contact_phone`}
                    placeholder={
                      allow_anonymous_shipments && allowShipment
                        ? null
                        : intl.formatMessage({
                            id: "new-request.schedule.phoneInfo",
                            defaultMessage: "Type phone number here...",
                          })
                    }
                    mandatory={!allow_anonymous_shipments && !isOffPlatformShipment}
                    disabled={
                      (allow_anonymous_shipments && allowShipment) ||
                      formState.disabledFields[`schedule.${isPickupOrDelivery}_contact_phone`]
                    }
                    validations={{
                      validate: {
                        ...(invitedModes?.parcel && {
                          maxCharacters: (v: any) => (v?.length || 0) <= 15 || "Maximum characters exceeded (15)",
                        }),
                      },
                    }}
                    onChange={(e) => {
                      setValue(`schedule.${isPickupOrDelivery}_contact_phone`, e.target.value);
                    }}
                    testId={
                      isPickupOrDelivery === "pickup"
                        ? testIds.REQUEST_FORM_SCHEDULE_PICKUP_CONTACT_PHONE
                        : testIds.REQUEST_FORM_SCHEDULE_DELIVERY_CONTACT_PHONE
                    }
                  />
                </div>
                <div className="w-192">
                  <FormField
                    id="id_pickup_contact_email"
                    label={intl.formatMessage({
                      id: "new-request.schedule.pickupContactEmail",
                      defaultMessage: "Email",
                    })}
                    placeholder={
                      allow_anonymous_shipments && allowShipment
                        ? null
                        : intl.formatMessage({
                            id: "new-request.schedule.emailInfo",
                            defaultMessage: "Type email here...",
                          })
                    }
                    type="textinput"
                    field_id={`schedule.${isPickupOrDelivery}_contact_email`}
                    onChange={(e) => {
                      setValue(`schedule.${isPickupOrDelivery}_contact_email`, e.target.value);
                    }}
                    disabled={
                      (allow_anonymous_shipments && allowShipment) ||
                      formState.disabledFields[`schedule.${isPickupOrDelivery}_contact_email`]
                    }
                    mandatory={!allow_anonymous_shipments && !isOffPlatformShipment}
                    testId={
                      isPickupOrDelivery === "pickup"
                        ? testIds.REQUEST_FORM_SCHEDULE_PICKUP_CONTACT_EMAIL
                        : testIds.REQUEST_FORM_SCHEDULE_DELIVERY_CONTACT_EMAIL
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </>
  );
}

export default PickupDeliveryContact;
